import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Empty, Spin } from 'antd';
import {
  AccountBookOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  FieldTimeOutlined,
  PayCircleOutlined,
  RightOutlined,
  SwapOutlined,
  SyncOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { fetchDashboardData, clearError } from '../features/dashboard/dashboardSlice';
import dayjs from 'dayjs';
import { getDashboardTimeRange } from '../utils';
import '../styles/dashboard.css';

const PAY_COLOR = '#2278F5';
const WITHDRAW_COLOR = '#50c317';

const HeroScenery = () => (
  <svg className="dashboard__hero-art" viewBox="0 0 760 150" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="dashHeroFade" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#fff" stopOpacity="0" />
        <stop offset="0.28" stopColor="#fff" stopOpacity="0.4" />
        <stop offset="0.52" stopColor="#fff" stopOpacity="1" />
      </linearGradient>
      <mask id="dashHeroMask">
        <rect width="760" height="150" fill="url(#dashHeroFade)" />
      </mask>
    </defs>
    <g mask="url(#dashHeroMask)">
      <g fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity="0.48">
        <path d="M336 35 q8 -7 16 0 q8 -7 16 0" />
        <path d="M390 25 q9 -7 18 0 q9 -7 18 0" />
      </g>
      <g>
        <path fill="#286fda" opacity="0.55" d="M0 150 L98 119 L178 126 L267 83 L316 103 L389 42 L438 88 L489 70 L551 111 L622 78 L760 124 L760 150 Z" />
        <path fill="#8dbff1" opacity="0.62" d="M0 150 L126 129 L218 98 L278 127 L374 76 L436 116 L505 90 L574 123 L649 99 L760 132 L760 150 Z" />
        <path fill="#d8eafa" opacity="0.72" d="M0 150 L156 135 L256 114 L338 138 L437 111 L514 137 L610 117 L690 137 L760 129 L760 150 Z" />
        <path fill="#fff" opacity="0.32" d="M344 70 L389 42 L411 71 L389 62 L374 73 L362 68 Z" />
        <path fill="#fff" opacity="0.25" d="M244 96 L267 83 L286 94 L270 91 L259 98 Z" />
      </g>
    </g>
  </svg>
);

const CardScenery = () => (
  <svg className="dash-card__art" viewBox="0 0 220 84" preserveAspectRatio="none" aria-hidden="true">
    <path opacity="0.13" d="M0 84 C28 69 47 48 70 53 C91 58 102 72 122 55 C142 38 151 20 169 32 C187 44 196 58 220 45 L220 84 Z" />
    <path opacity="0.24" d="M0 84 C36 77 58 63 83 68 C108 73 118 81 145 64 C174 46 190 63 220 57 L220 84 Z" />
  </svg>
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { thirdPartyStats, charts, isLoading, error } = useSelector((state) => state.dashboard);

  // 首次加载完成后，后台 30 秒轮询不再整页显示加载态
  const [firstLoaded, setFirstLoaded] = useState(false);
  const wasLoading = useRef(false);

  useEffect(() => {
    const load = () => {
      const { today } = getDashboardTimeRange();
      dispatch(fetchDashboardData({ startTs: today.startTs, endTs: today.endTs }));
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (error) dispatch(clearError());
  }, [error, dispatch]);

  useEffect(() => {
    if (wasLoading.current && !isLoading) setFirstLoaded(true);
    wasLoading.current = isLoading;
  }, [isLoading]);

  if (error) return <Alert message="加载失败" description={error} type="error" showIcon />;

  const formatAmount = (val) => val ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
  const formatCount = (val) => Number(val || 0).toLocaleString('en-US');

  const payChartData = charts?.thirdPartyChart?.payChart || [];
  const withdrawChartData = charts?.thirdPartyChart?.withdrawChart || [];
  const hasChartData = payChartData.length > 0 || withdrawChartData.length > 0;

  const statCards = [
    { label: '商户余额', tone: 'blue', icon: <WalletOutlined />, value: thirdPartyStats?.merchantTotalBalance },
    { label: '待结算金额', tone: 'amber', icon: <FieldTimeOutlined />, value: thirdPartyStats?.totalWaitSettleAmount },
    { label: '待代付金额', tone: 'red', icon: <PayCircleOutlined />, value: thirdPartyStats?.todayWaitWithdrawAmount },
    { label: '充值总额', tone: 'green', icon: <ArrowDownOutlined />, value: thirdPartyStats?.totalMerchantRecharge },
    { label: '提款总额', tone: 'violet', icon: <ArrowUpOutlined />, value: thirdPartyStats?.totalMerchantWithdraw },
  ];

  const panels = [
    {
      title: '收款订单',
      icon: <AccountBookOutlined />,
      iconTone: '',
      path: '/third-party/orders',
      metrics: [
        { label: '待审核', value: formatCount(thirdPartyStats?.pendingPayCount), tone: 'pending' },
        { label: '今日金额', value: formatAmount(thirdPartyStats?.todayPayAmount) },
        { label: '今日笔数', value: formatCount(thirdPartyStats?.todayPayCount) },
        { label: '总金额', value: formatAmount(thirdPartyStats?.totalPayAmount) },
      ],
    },
    {
      title: '代付订单',
      icon: <SwapOutlined />,
      iconTone: 'dash-panel__icon--green',
      path: '/third-party/withdrawals',
      metrics: [
        { label: '待审核', value: formatCount(thirdPartyStats?.pendingWithdrawCount), tone: 'pending' },
        { label: '今日金额', value: formatAmount(thirdPartyStats?.todayWithdrawAmount) },
        { label: '今日笔数', value: formatCount(thirdPartyStats?.todayWithdrawCount) },
        { label: '总金额', value: formatAmount(thirdPartyStats?.totalWithdrawAmount) },
      ],
    },
  ];

  const chartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e8ee',
      borderWidth: 1,
      padding: [10, 14],
      textStyle: { color: '#1f2937', fontSize: 12 },
      valueFormatter: (val) => formatAmount(val),
    },
    legend: { show: false },
    grid: { left: 6, right: 10, bottom: 0, top: 28, containLabel: true },
    xAxis: {
      type: 'category',
      data: payChartData.map(item => dayjs(item.date).format('MM-DD')),
      axisLine: { lineStyle: { color: '#dfe6ef' } },
      axisTick: { show: false },
      axisLabel: { color: '#8793a5', fontSize: 11, margin: 10 },
    },
    yAxis: {
      type: 'value',
      splitNumber: 6,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#8793a5',
        fontSize: 11,
        formatter: (val) => Number(val).toLocaleString('en-US'),
      },
      splitLine: { lineStyle: { color: '#edf1f6', type: 'dashed' } },
    },
    series: [
      {
        name: '收款',
        type: 'bar',
        barWidth: 24,
        barGap: '20%',
        data: payChartData.map(item => item.amount || 0),
        itemStyle: { color: PAY_COLOR, borderRadius: [3, 3, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: '#788496',
          fontSize: 10,
          formatter: (p) => p.value > 0 ? Number(p.value).toLocaleString('en-US') : '',
        },
      },
      {
        name: '代付',
        type: 'bar',
        barWidth: 24,
        data: withdrawChartData.map(item => item.amount || 0),
        itemStyle: { color: WITHDRAW_COLOR, borderRadius: [3, 3, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: '#788496',
          fontSize: 10,
          formatter: (p) => p.value > 0 ? Number(p.value).toLocaleString('en-US') : '',
        },
      },
    ],
  };

  return (
    <div className="dashboard">
      <div className="dashboard__hero">
        <HeroScenery />
        <div className="dashboard__hero-text">
          <h1 className="dashboard__hero-title">综合看板</h1>
          <p className="dashboard__hero-subtitle">第三方支付运营数据</p>
        </div>
        {isLoading && firstLoaded && (
          <div className="dashboard__hero-meta">
            <span className="dashboard__syncing">
              <SyncOutlined spin />
              更新中
            </span>
          </div>
        )}
      </div>

      {isLoading && !firstLoaded ? (
        <div className="dashboard__loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="dashboard__stats">
            {statCards.map((card) => (
              <div key={card.label} className={`dash-card dash-card--${card.tone}`}>
                <CardScenery />
                <div className="dash-card__head">
                  <span className="dash-card__icon">{card.icon}</span>
                  <span className="dash-card__label">{card.label}</span>
                </div>
                <div className="dash-card__value">{formatAmount(card.value)}</div>
                {/* <div className="dash-card__unit">金额（元）</div> */}
              </div>
            ))}
          </div>

          <div className="dashboard__panels">
            {panels.map((panel) => (
              <section key={panel.title} className="dash-panel">
                <div className="dash-panel__head">
                  <div className="dash-panel__title">
                    <span className={`dash-panel__icon ${panel.iconTone}`}>{panel.icon}</span>
                    {panel.title}
                  </div>
                  <button type="button" className="dash-panel__more" onClick={() => navigate(panel.path)}>
                    查看更多
                    <RightOutlined style={{ fontSize: 11 }} />
                  </button>
                </div>

                <div className="dash-panel__metrics">
                  {panel.metrics.map((metric) => (
                    <div key={metric.label} className="dash-metric">
                      <div className="dash-metric__label">{metric.label}</div>
                      <div className={`dash-metric__value ${metric.tone ? `dash-metric__value--${metric.tone}` : ''}`}>
                        {metric.value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <section className="dash-panel dash-panel--spaced">
            <div className="dash-panel__head">
              <div className="dash-panel__title">
                <span className="dash-panel__icon dash-panel__icon--plain"><BarChartOutlined /></span>
                7日收款/代付趋势
              </div>
              <div className="dash-panel__legend">
                <span className="dash-legend">
                  <i className="dash-legend__dot" style={{ background: PAY_COLOR }} />
                  收款
                </span>
                <span className="dash-legend">
                  <i className="dash-legend__dot" style={{ background: WITHDRAW_COLOR }} />
                  代付
                </span>
              </div>
            </div>
            <div className="dash-panel__chart">
              {hasChartData ? (
                <ReactECharts className="dash-trend-chart" option={chartOption} notMerge />
              ) : (
                <Empty className="dash-panel__empty" description="暂无趋势数据" />
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default Dashboard;
